import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Lock, ArrowRight } from "lucide-react";
import { StepReferrer, StepClient, StepCase, StepConflict, StepReview } from "@/components/intake/IntakeSteps";
import { StepIndicator, DoneScreen } from "@/components/intake/IntakeChrome";

const STEPS = ["Referrer", "Client", "Case", "Conflict Check", "Review"];

export default function CaseIntake() {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [f, setF] = useState({
    referrer_name: "", referrer_email: "", referrer_org: "",
    client_name: "", is_veteran: false, case_type: "medical_malpractice",
    summary: "", urgency: "standard", conflict_confirmed: false,
  });
  const set = (k, v) => setF({ ...f, [k]: v });
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/public/intake", {
        referrer_name: f.referrer_name, referrer_email: f.referrer_email, referrer_org: f.referrer_org,
        client_name: f.client_name, case_type: f.case_type, is_veteran: f.is_veteran,
        summary: f.summary, urgency: f.urgency,
      });
      setDone(true);
      toast.success("Encrypted referral submitted. Response within 48 hours.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) return <DoneScreen />;

  return (
    <div className="fade-in-up">
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-center gap-2 mb-3"><Lock className="w-4 h-4 text-[#0A192F]" /><span className="text-caption">SECURE ENCRYPTED INTAKE</span></div>
        <h1 className="text-4xl lg:text-5xl font-serif font-bold text-[#0A192F] leading-tight mb-4">Refer a case for merit review.</h1>
        <p className="text-slate-600 mb-10">Encrypted submission — 48-hour first response. Please avoid pasting full medical records; upload will be enabled after conflict clearance in the secure portal.</p>

        <StepIndicator steps={STEPS} step={step} />

        <div className="card-federal p-8">
          {step === 0 && <StepReferrer f={f} set={set} />}
          {step === 1 && <StepClient f={f} set={set} />}
          {step === 2 && <StepCase f={f} set={set} />}
          {step === 3 && <StepConflict f={f} set={set} />}
          {step === 4 && <StepReview f={f} />}

          <div className="flex justify-between mt-8">
            <button onClick={prev} disabled={step === 0} className="border border-slate-300 px-5 py-2.5 text-[12px] uppercase tracking-widest disabled:opacity-40" data-testid="intake-prev">Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-navy px-6 py-2.5 text-[12px] uppercase tracking-widest inline-flex items-center gap-2" data-testid="intake-next">Continue <ArrowRight className="w-3.5 h-3.5" /></button>
            ) : (
              <button disabled={busy || !f.conflict_confirmed} onClick={submit} className="btn-crimson px-6 py-2.5 text-[12px] uppercase tracking-widest disabled:opacity-50" data-testid="intake-submit">{busy ? "Submitting…" : "Submit Encrypted Referral"}</button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
